import { type GetAllClassParams } from '~entities/classes/api/get-all-classes-with-search';
import type { TotalEnrollmentsOverTimeParams } from '~entities/dashboard/api/get-total-enrollments-over-time';
import type { GetAllEventsParams } from '~entities/events/api/get-all-events-with-search';
import type { GetEventPaymentHistoryParams } from '~entities/events/api/get-event-payment-history';
import type { GetParentPaymentHistoryParams } from '~entities/parent/api/get-parent-payment-history';
import type { GetPaymentRunReviewParams } from '~entities/payment/api/reporting-payment-run-review';
import type { GetAllRecitalsParams } from '~entities/recital-manager/api/get-all-recitals';
import type { GetAllStudentsParams } from '~entities/student/api/get-all-students-with-search';
import { type GetTrialStudentProps } from '~entities/student/api/get-all-trial-students';
import { type GetStudentPaymentHistoryParams } from '~entities/student/api/get-student-payment-history';

import type { GetAnnouncementsParams } from '~entities/announcements/model/types';

export const KEYS = {
  ACCOUNTS_RECEIVABLE_DUE_DATE_GROUPING:
    'accounts-receivable-due-date-grouping',
  ACCOUNTS_RECEIVABLE_STUDENT_GROUPING: 'accounts-receivable-student-grouping',
  ACCOUNTS_RECEIVABLE: 'accounts-receivable',
  MANUAL_ACCOUNTS_RECEIVABLE_TOTAL: 'manual-accounts-receivable-total',
  MANUAL_ACCOUNTS_RECEIVABLE_DUE_DATE_GROUPING:
    'manual-accounts-receivable-due-date-grouping',
  MANUAL_ACCOUNTS_RECEIVABLE_STUDENT_GROUPING:
    'manual-accounts-receivable-student-grouping',
  ACTIVE_STUDENTS_CLASS_COUNT_REPORT: 'active-students-class-count-report',
  ACTIVE_STUDENTS_NO_ATTENDANCE: 'active-students-no-attendance',
  ACTIVE_TUITION_DETAILS: 'active-tuition-details',
  ACTS: 'acts',
  AGE_DISTRIBUTION: 'age-distribution',
  ALLOWED_PAYMENT_METHODS: 'allowed-payment-methods',
  ENROLLMENT_PAYMENT_METHODS: 'enrollment-payment-methods',
  ATTENDANCE_ANALYSIS: 'attendance-analysis',
  ATTENDANCE_REPORT: 'attendance-report',
  ATTENDANCE_SESSION_CSV_JOB_STATUS: 'attendance-session-csv-job-status',
  ATTENDANCE_SESSION_CSV_JOBS: 'attendance-session-csv-jobs',
  ATTENDANCE_SESSION_PDF_JOB_STATUS: 'attendance-session-pdf-job-status',
  ATTENDANCE_SESSION_PDF_JOBS: 'attendance-session-pdf-jobs',
  ATTENDANCE_STATUS: 'attendance-status',
  ATTENDANCE: 'attendance',
  AUDIT_EVENTS: 'audit-events',
  AUDIT_LOGS: 'audit-logs',
  AUDIT_RESOURCES: 'audit-resources',
  ANNOUNCEMENTS: 'announcements',
  PORTAL_BANNER_ACTIVE: 'portal-banner-active',
  PORTAL_BANNERS: 'portal-banners',
  AUTH: 'auth',
  AVERAGE_ATTENDANCE: 'average-attendance',
  AVERAGE_DROP_RATE: 'average-drop-rate',
  BIRTHDAYS_REPORT: 'birthdays-report',
  STUDENTS_BY_AGE_REPORT: 'students-by-age-report',
  INACTIVE_STUDENTS_REPORT: 'inactive-students-report',
  BOOKING_LINK: 'booking-link',
  BULK_CHARGE_HISTORY: 'bulk-charge-history',
  BULK_IMPORT_CLASSES_STATUS: 'bulk-import-classes-status',
  CARD: 'card',
  PARENT_PAYMENT_METHOD_SETUP: 'parent-payment-method-setup',
  CHANGE_PASSWORD: 'change-password',
  CLASS_ANALYTICS: 'class-analytics',
  CLASS_LIST_VIEW: 'class-list-view',
  CLASS_CAPACITY: 'class-capacity',
  CLASS_ENROLLMENTS_WITH_STUDENT_DETAILS_REPORT:
    'class-enrollments-with-student-details-report',
  NEW_VS_RETURNING_STUDENTS_REPORT: 'new-vs-returning-students-report',
  CLASS_GROUPS_REPORT: 'class-groups-report',
  SESSION_STUDENTS_ENROLLMENTS_REPORT: 'session-students-enrollments-report',
  CLASS_AND_EVENT_ENROLLMENTS_REPORT: 'class-and-event-enrollments-report',
  CLASS_PERFORMANCE_LOW_ENROLLMENT: 'class-performance-low-enrollment',
  CLASS_PERFORMANCE_NEAR_CAPACITY: 'class-performance-near-capacity',
  CLASS_PERFORMANCE: 'class-performance',
  CLASS_MEDICAL: 'class-medical',
  CLASS_SESSIONS: 'class-sessions',
  CLASSES_CSV: 'classes-csv',
  CLASSES_WITH_MOST_OPENING: 'classes-with-most-opening',
  CLASSES: 'classes',
  COLLECTED_REVENUE_MONTHLY_TREND: 'collected-revenue-monthly-trend',
  COLLECTED_REVENUE_TOTAL: 'collected-revenue-total',
  CONFLICT_RULES: 'conflict-rules',
  CONFLICTS: 'conflicts',
  COUNTRIES_ALL: 'countries-all',
  CREDENTIALS: 'credentials',
  CURRENT_MONTH_ENROLMENT: 'current-month-enrolment',
  DEMO_MODE_STATUS: 'demo-mode-status',
  V2_MODE_STATUS: 'v2-mode-status',
  CURRENT_USER: 'current-user',
  PUBLIC_CLASS_LOOKUP: 'public-class-lookup',
  DASHBOARD_OUTSTANDING_OVERVIEW: 'dashboard-outstanding-overview',
  DASHBOARD_SCHEDULED_UPCOMING_OVERVIEW:
    'dashboard-scheduled-upcoming-overview',
  DASHBOARD_STATUS: 'dashboard-status',
  DAYS_PAST_DUE_OVERDUE_PAYMENTS: 'days-past-due-overdue-payments',
  DISCOUNT_BY_ENTITY: 'discount-by-entity',
  DISCOUNT: 'discount',
  DRAFTS: 'drafts',
  DROP_RATE_ANALYSIS: 'drop-rate-analysis',
  DRT_INTEGRATIONS: 'drt-integrations',
  EMAIL_TEMPLATE: 'email-template',
  EMAIL_TOGGLE_PREVIEW: 'email-toggle-preview',
  EMAIL_TOGGLES: 'email-toggles',
  EMAIL_COMPOSER_DEFAULTS: 'email-composer-defaults',
  EMAIL_CAMPAIGN_TEMPLATES: 'email-campaign-templates',
  EMAIL_CAMPAIGNS: 'email-campaigns',
  UNLAYER_TEMPLATES: 'unlayer-templates',
  UNLAYER_IDENTITY: 'unlayer-identity',
  SYSTEM_EMAIL_TEMPLATE: 'system-email-template',
  EMBED_LINK: 'embed-link',
  ENROLLMENT_GROWTH: 'enrollment-growth',
  ACTIVE_CLASSES_WEEK_COMPARISON: 'active-classes-week-comparison',
  ENROLLMENT_PERCENTAGE_OVER_LAST_MONTH:
    'enrollment-percentage-over-last-month',
  ENROLLMENT_TO_STUDENT_RATIO: 'enrollment-to-student-ratio',
  ENROLLMENT_TOTAL_PAYMENTS: 'enrollment-total-payments',
  EVENTS_CSV: 'events-csv',
  EVENT_PAYMENT_HISTORY: 'event-payment-history',
  EVENTS: 'events',
  EXPIRING_CARDS: 'expiring-cards',
  EXPORT_FORMAT: {
    CSV: 'csv',
    EXCEL: 'excel',
    PDF: 'pdf',
    JSON: 'json',
  },
  FAILED_PAYMENTS: 'failed-payments',
  FAMILY_BY_STUDENT: 'family-by-student',
  FAMILY_BY_PARENT: 'family-by-parent',
  FAMILY_HUB: 'family-hub',
  FILTERED_INVOICES: 'filtered-invoices',
  FULLY_BOOKED_CLASSES: 'fully-booked-classes',
  GHL_CALENDARS: 'ghl-calendars',
  GHL_CONTACTS: 'ghl-contacts',
  GHL_LOCATION_DETAILS: 'ghl-location-details',
  GHL_LOCATION_TAGS: 'ghl-location-tags',
  GROUPS: 'groups',
  HOURLY_TUITION_BILLING: 'hourly-tuition-billing',
  CLASS_COUNT_TUITION_BILLING: 'class-count-tuition-billing',
  INSTRUCTORS: 'instructors',
  INVOICE_COUPONS: 'invoice-coupons',
  INVOICE_LINE_ITEMS: 'invoice-line-items',
  ISSUED_REFUND: 'issued-refunds',
  LOCATIONS: 'locations',
  LOGIN_IMAGE: 'login-image',
  NINETY_PERCENT_FULL_CLASSES: 'ninety-percent-full-classes',
  NOTES: 'notes',
  OUTGOING_EMAIL_LOG_SCENARIOS: 'outgoing-email-log-scenarios',
  OUTGOING_EMAIL_LOGS: 'outgoing-email-logs',
  MOBILE_INVENTOR_PUSH: 'mobile-inventor-push',
  MOBILE_INVENTOR_PUSH_CONTENT: 'mobile-inventor-push-content',
  MOBILE_INVENTOR_PUSH_SCENARIOS: 'mobile-inventor-push-scenarios',
  OUTSTANDING_AND_OVERDUE: 'outstanding-and-overdue',
  OUTSTANDING_BALANCE: 'outstanding-balance',
  PARENT_FORM_CONFIG: 'parent-form-config',
  FORM_SUBMISSIONS: 'form-submissions',
  FORM_SUBMISSION: 'form-submission',
  FORM_CONFIGS: 'form-configs',
  FORM_TEMPLATES: 'form-templates',
  FORM_PUBLIC_CONFIG: 'form-public-config',
  PARENT_OUTSTANDING_BALANCES: 'parent-outstanding-balances',
  PARENT_PAYMENTS_HISTORY: 'parent-payments-history',
  PARENT_TAGS: 'parent-tags',
  PARENT_WALLET_TRANSACTIONS: 'parent-wallet-transactions',
  PAYABLE_INVOICES: 'payable-invoices',
  TAKE_PAYMENT_PREVIEW: 'take-payment-preview',
  PARENT: 'parent',
  PARENTS_LIST: 'parents-list',
  PARENTS_ONBOARDING_BATCHES_BY_ID: 'parents-onboarding-batches-by-id',
  PARENTS_ONBOARDING_BATCHES: 'parents-onboarding-batches',
  PARENTS: 'parents',
  PARENTS_BULK_IMPORT_STATUS: 'parents-bulk-import-status',
  PARENTS_BULK_IMPORT_JOBS: 'parents-bulk-import-jobs',
  PAUSED_TUITION_DETAILS: 'paused-tuition-details',
  PAYMENT_DETAILS: 'payment-details',
  PAYMENT_HEALTH: 'payment-health',
  PAYMENT_HISTORY: 'payment-history',
  PAYMENT_METHOD: 'payment-method',
  PAYMENT_PROVIDER_STATEMENT: 'payment-provider-statement',
  TRANSACTION_CODES_REPORT: 'transaction-codes-report',
  PAYMENT_TRANSACTION_LINE_ITEMS: 'payment-transaction-line-items',
  PAYMENT_TRANSACTION_LIST: 'payment-transaction-list',
  PAYMENT_RUN_REVIEW: 'payment-run-review',
  PAYMENT_RUN_REVIEW_DETAIL: 'payment-run-review-detail',
  PAYMENTS_BY_CLASS: 'payments-by-class',
  PAYMENTS_BY_DUE_DATE_REPORT: 'payments-by-due-date-report',
  PAYMENTS_BY_DUE_DATE: 'payments-by-due-date',
  PAYMENTS_BY_SESSION: 'payments-by-session',
  PENDING_PAYMENTS: 'pending-payments',
  PERFORMANCES: 'performances',
  PERMISSIONS: 'permissions',
  PRIVATE_LESSON_BOOKINGS: 'private-lesson-bookings',
  PRIVATE_LESSON_SLOTS: 'private-lesson-slots',
  POLICIES_CSV: 'policies-csv',
  POLICIES: 'policies',
  RECITALS: 'recitals',
  RELEASES: 'releases',
  REMAINING_CREDIT: 'remaining-credit',
  RESET_PASSWORD: 'reset-password',
  REVENUE_BY_LOCATION_REPORT: 'revenue-by-location-report',
  REVENUE_BY_LOCATION: 'revenue-by-location',
  REVENUE_OVER_TIME_PERIOD: 'revenue-over-time-period',
  REVENUE_SNAPSHOT: 'revenue-snapshot',
  REVENUE_TREND: 'revenue-trend',
  ROOMS: 'rooms',
  ROOM_OVERVIEW: 'room-overview',
  SCHEDULED_DROPS: 'scheduled-drops',
  SCHEDULED_UPCOMING_PAYMENTS: 'scheduled-upcoming-payments',
  SEARCH: 'search',
  SESSION_BILLING_BREAKDOWN: 'session-billing-breakdown',
  SESSION_BILLING_BREAKDOWN_V2: 'session-billing-breakdown-v2',
  DROPPED_CLASS_REPORT: 'dropped-class-report',
  SESSION_ENROLLMENT_REPORT: 'session-enrollment-report',
  SESSION_FEE_STATUS: 'session-fee-status',
  SESSION_PAYMENTS_DETAILS: 'session-payments-details',
  SESSIONS: 'sessions',
  SESSION_LINKED_COUNT: 'session-linked-count',
  SHOWS: 'shows',
  SIZING: 'sizing',
  STATS: 'stats',
  STRIPE: 'stripe',
  STRIPE_CREDENTIAL_ENVIRONMENT: 'stripe-credential-environment',
  STUDENT_CLASS_HISTORY: 'student-class-history',
  STUDENT_CONTACTS: 'student-contacts',
  STUDENT_COUNT: 'student-count',
  STUDENT_DISTRIBUTION: 'student-distribution',
  STUDENT_ENROLMENT_PERCENTAGE: 'student-enrolment-percentage',
  STUDENT_PAYMENT_HISTORY: 'student-payment-history',
  STUDENT_SUBSCRIPTION_INVOICES: 'student-subscription-invoices',
  STUDENTS_CSV: 'students-csv',
  STUDENTS_IN_GROUP_BY_SESSION: 'students-in-group-by-session',
  STUDENTS: 'students',
  STUDIO_DEFAULT_PAYMENT_PROVIDER: 'studio-default-payment-provider',
  STUDIO_DETAILS_ALL: 'studio-details-all',
  STUDIO_DETAILS: 'studio-details',
  STUDIO_PREFLIGHT_CHECK: 'studio-preflight-check',
  STUDIO_LOGO_IMAGE: 'studio-logo-image',
  STUDIO: 'studio',
  STUDIOS: 'studios',
  TAGS: 'tags',
  TOP_10_ENROLLED_STUDENTS: 'top-10-enrolled-students',
  TOP_SPENDING_PARENTS: 'top-spending-parents',
  TOTAL_ACTIVE_CLASSES: 'total-active-classes',
  TOTAL_ENROLLMENTS_OVER_TIME: 'total-enrollments-over-time',
  TOTAL_OUTSTANDING_REVENUE: 'total-outstanding-revenue',
  TOTAL_SCHEDULED_UPCOMING_REVENUE: 'total-scheduled-upcoming-revenue',
  TRANSACTION_CODE: 'transaction-code',
  TRANSACTION_DETAILS_REPORT: 'transaction-details-report',
  UNSYNCED_CUSTOMER_EMAILS: 'unsynced-customer-emails',
  TRANSACTION_FEES_ALL: 'transaction-fees-all',
  TRANSACTION_FEES_SETTINGS: 'transaction-fees-settings',
  CLASS_GENRE_REPORT: 'class-genre-report',
  TRIAL_CLASS_REPORT: 'trial-class-report',
  TRIAL_CONVERSION: 'trial-conversion',
  TRIAL_STUDENTS: 'trial-students',
  TRIAL_LIFECYCLE_CONFLICTS: 'trial-lifecycle-conflicts',
  TUITION_DETAILS: 'tuition-details',
  UNLINKED_CRM_USERS: 'unlinked-crm-users',
  UPCOMING_SUBSCRIPTION_INVOICES: 'upcoming-subscription-invoices',
  USER_ROLES: 'user-roles',
  USERS: 'all-users',
  WAIT_LIST_STATUS: 'wait-list-status',
  WAIT_LIST_SUMMARY: 'wait-list-summary',
  WALLET_BALANCE: 'wallet-balance',
  MAKEUP_SETTINGS: 'makeup-settings',
  MAKEUP_BALANCES: 'makeup-balances',
  MAKEUP_BY_CLASS_DATE: 'makeup-by-class-date',
  MAKEUP_BY_SOURCE: 'makeup-by-source',
  MAKEUP_BY_STUDENT: 'makeup-by-student',
  MAKEUP_TARGETS: 'makeup-targets',
  MAKEUP_OCCURRENCE_CAPACITY: 'makeup-occurrence-capacity',
  PORTAL_DIGEST_SETTINGS: 'portal-digest-settings',
  PORTAL_DIGEST_PREVIEW: 'portal-digest-preview',
  PORTAL_DIGEST_PIPELINES: 'portal-digest-pipelines',
} as const;

export type ExportFormat =
  (typeof KEYS.EXPORT_FORMAT)[keyof typeof KEYS.EXPORT_FORMAT];

export const QUERY_KEYS = {
  CURRENT_USER: () => [KEYS.CURRENT_USER],
  RESET_USER_PASSWORD: (params: { userId: string }) => [
    params.userId,
    KEYS.RESET_PASSWORD,
  ],
  ALL_USERS: () => [KEYS.USERS],
  USER_ROLES: () => [KEYS.USER_ROLES],
  UNLINKED_CRM_USERS: () => [KEYS.UNLINKED_CRM_USERS],
  CLASSES: (params?: GetAllClassParams) => [
    KEYS.CLASSES,
    params ? KEYS.SEARCH : '',
    ...(params
      ? Object.entries(params)
          .filter(([, value]) => value !== undefined)
          .map(([key, value]) => `${key}:${value}`)
      : []),
  ],

  CLASS: (params: { classId: string; studioId?: string }) => [
    KEYS.CLASSES,
    ...Object.values(params).filter(Boolean).join('').split(''),
  ],

  PRIVATE_LESSON_SLOTS: (params: {
    offeringId: string;
    startDate: string;
    endDate: string;
  }) => [
    KEYS.PRIVATE_LESSON_SLOTS,
    params.offeringId,
    params.startDate,
    params.endDate,
  ],

  PRIVATE_LESSON_BOOKINGS: (params?: {
    offeringId?: string;
    studentId?: string;
    seriesId?: string;
    timing?: string;
  }) => [
    KEYS.PRIVATE_LESSON_BOOKINGS,
    ...(params
      ? Object.entries(params)
          .filter(([, value]) => value !== undefined)
          .map(([key, value]) => `${key}:${value}`)
      : []),
  ],

  DROP_CLASS: (params: {
    classId: string;
    studentId: string;
    studioId: string;
  }) =>
    [
      KEYS.CLASSES,
      params.classId,
      KEYS.STUDENTS,
      params.studentId,
      KEYS.STUDIOS,
      params.studioId,
    ].filter(Boolean),

  TRANSFER_CLASS: (params: {
    currentClassId: string;
    newClassId: string;
    studentId: string;
    studioId: string;
  }) =>
    [
      KEYS.CLASSES,
      params.currentClassId,
      params.newClassId,
      KEYS.STUDENTS,
      params.studentId,
      KEYS.STUDIOS,
      params.studioId,
    ].filter(Boolean),

  TOTAL_ENROLLMENTS_OVER_TIME: (params?: TotalEnrollmentsOverTimeParams) => [
    KEYS.TOTAL_ENROLLMENTS_OVER_TIME,
    params ? KEYS.SEARCH : '',
    ...(params
      ? Object.entries(params)
          .filter(Boolean)
          .map(([key, value]) => `${key}:${value}`)
      : []),
  ],

  EVENTS: (params?: GetAllEventsParams) => [
    KEYS.EVENTS,
    params ? KEYS.SEARCH : '',
    ...(params ? Object.values(params).filter(Boolean).join('').split('') : []),
  ],

  EVENT: (id: string, ...params: Array<string>) => [KEYS.EVENTS, id, ...params],

  INSTRUCTORS: [KEYS.INSTRUCTORS],

  INSTRUCTOR: (id: string, ...params: Array<string>) => [
    KEYS.INSTRUCTORS,
    id,
    ...params,
  ],

  LOCATIONS: [KEYS.LOCATIONS],

  LOCATION: (id: string, ...params: Array<string>) => [
    KEYS.LOCATIONS,
    id,
    ...params,
  ],

  ROOMS: [KEYS.ROOMS],

  ROOM: (id: string, ...params: Array<string>) => [KEYS.ROOMS, id, ...params],

  ROOM_OVERVIEW: (params: { date: string; instructorIds?: Array<string> }) => [
    KEYS.ROOM_OVERVIEW,
    params.date,
    ...(params.instructorIds?.slice().sort() ?? []),
  ],

  GROUPS: [KEYS.GROUPS],

  GROUP: (id: string, ...params: Array<string>) => [KEYS.GROUPS, id, ...params],

  PARENTS: [KEYS.PARENTS],

  PARENTS_BULK_IMPORT_STATUS: (jobId: string) => [
    KEYS.PARENTS_BULK_IMPORT_STATUS,
    jobId,
  ],

  PARENT: (id: string, ...params: Array<string>) => [
    KEYS.PARENTS,
    id,
    ...params,
  ],

  POLICIES: [KEYS.POLICIES],

  POLICY: (id: string, ...params: Array<string>) => [
    KEYS.POLICIES,
    id,
    ...params,
  ],

  RECITALS: (params?: GetAllRecitalsParams) => [
    KEYS.RECITALS,
    params ? KEYS.SEARCH : '',
    ...(params ? Object.values(params).filter(Boolean).join('').split('') : []),
  ],
  RECITAL: (id: string, ...params: Array<string>) => [
    KEYS.RECITALS,
    id,
    ...params,
  ],

  SESSIONS: [KEYS.SESSIONS],

  SESSION: (id: string, ...params: Array<string>) => [
    KEYS.SESSIONS,
    id,
    ...params,
  ],

  STUDENTS: [KEYS.STUDENTS],

  STUDENT_RELATIONS: (parentId: string) => [KEYS.STUDENTS, parentId],

  STUDENT: (id: string, ...params: Array<string>) => [
    KEYS.STUDENTS,
    id,
    ...params,
  ],

  STUDENTS_WITH_SEARCH: (params?: GetAllStudentsParams) => [
    KEYS.STUDENTS,
    params ? KEYS.SEARCH : '',
    ...(params
      ? Object.entries(params)
          .filter(Boolean)
          .map(([key, value]) => `${key}:${value}`)
      : []),
  ],

  STUDENT_PAYMENT_HISTORY: (params?: GetStudentPaymentHistoryParams) => [
    KEYS.STUDENT_PAYMENT_HISTORY,
    params ? KEYS.SEARCH : '',
    ...(params
      ? Object.entries(params)
          .filter(Boolean)
          .map(([key, value]) => `${key}:${value}`)
      : []),
  ],

  EVENT_PAYMENT_HISTORY: (params?: GetEventPaymentHistoryParams) => [
    KEYS.EVENT_PAYMENT_HISTORY,
    params ? KEYS.SEARCH : '',
    ...(params
      ? Object.entries(params)
          .filter(Boolean)
          .map(([key, value]) => `${key}:${value}`)
      : []),
  ],

  STUDIOS: [KEYS.STUDIOS],

  STUDIO: (id: string, ...params: Array<string>) => [
    KEYS.STUDIOS,
    id,
    ...params,
  ],

  TAGS: [KEYS.TAGS],

  TAG: (id: string, ...params: Array<string>) => [KEYS.TAGS, id, ...params],

  USERS: () => ['users'],

  USER: (userId: string) => [...QUERY_KEYS.USERS(), userId],

  USER_DETAILS: () => [...QUERY_KEYS.USERS(), 'details'],

  PERMISSIONS: () => [KEYS.PERMISSIONS],

  ATTENDANCE_ANALYSIS: (params: { studioId: string; groupBy: string }) => [
    KEYS.ATTENDANCE_ANALYSIS,
    params.studioId,
    params.groupBy,
  ],

  DROP_RATE_ANALYSIS: (params: { sessionId: string; groupBy: string }) => [
    KEYS.DROP_RATE_ANALYSIS,
    params.sessionId,
    params.groupBy,
  ],

  AVERAGE_ATTENDANCE: (params: { sessionId: string }) => [
    KEYS.AVERAGE_ATTENDANCE,
    params.sessionId,
  ],

  AVERAGE_DROP_RATE: (params: { sessionId: string }) => [
    KEYS.AVERAGE_DROP_RATE,
    params.sessionId,
  ],

  SESSION_BILLING_BREAKDOWN: (params: {
    sessionId: string;
    fromDate: string;
    toDate: string;
  }) => [
    KEYS.HOURLY_TUITION_BILLING,
    KEYS.SESSION_BILLING_BREAKDOWN,
    params.sessionId,
    params.fromDate,
    params.toDate,
  ],

  SESSION_BILLING_BREAKDOWN_V2: (params: {
    sessionId: string;
    fromDate: string;
    toDate: string;
  }) => [
    KEYS.HOURLY_TUITION_BILLING,
    KEYS.SESSION_BILLING_BREAKDOWN_V2,
    params.sessionId,
    params.fromDate,
    params.toDate,
  ],

  PARENT_PAYMENTS_HISTORY: (params: GetParentPaymentHistoryParams) => [
    KEYS.PARENT_PAYMENTS_HISTORY,
    params.parentId,
    params.startDate,
    params.endDate,
    params.dateField,
    params.sortOrder,
  ],

  UPCOMING_PAYMENTS: (params: {
    page?: number;
    limit?: number;
    dueDate: string;
  }) => ['upcoming-payments', params.page, params.limit, params.dueDate],

  FAILED_PAYMENTS: (params: {
    page?: number;
    limit?: number;
    startDate: string;
    endDate: string;
    search?: string;
  }) => [
    'failed-payments',
    params.page,
    params.limit,
    params.startDate,
    params.endDate,
    params.search,
  ],

  PAYMENT_RUN_REVIEW: (params: GetPaymentRunReviewParams) => [
    KEYS.PAYMENT_RUN_REVIEW,
    params.startDate,
    params.endDate,
    params.search ?? '',
    params.page ?? 1,
    params.limit ?? 25,
  ],

  PAYMENT_RUN_REVIEW_DETAIL: (
    invoiceId: string | null,
    startDate: string,
    endDate: string,
  ) => [KEYS.PAYMENT_RUN_REVIEW_DETAIL, invoiceId, startDate, endDate] as const,

  DASHBOARD_OUTSTANDING_OVERVIEW: (params?: {
    page?: number;
    limit?: number;
    sortBy?: 'daysOverdue' | 'parentName' | 'studentName' | 'dueDate';
    sortOrder?: 'asc' | 'desc';
    q?: string;
  }) => [
    KEYS.DASHBOARD_OUTSTANDING_OVERVIEW,
    params?.page ?? 1,
    params?.limit ?? 20,
    params?.sortBy ?? 'daysOverdue',
    params?.sortOrder ?? 'desc',
    params?.q ?? '',
  ],

  FILTERED_INVOICES: (params?: {
    startDate?: string;
    endDate?: string;
    dateField?: string;
    statuses?: Array<string>;
    finalAmount?: number;
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: string;
    studentSearch?: string;
  }) => [
    KEYS.FILTERED_INVOICES,
    ...(params
      ? Object.entries(params)
          .filter(([, value]) => {
            if (typeof value === 'string') return value !== '';
            if (typeof value === 'number') return true;
            return Array.isArray(value) && value.length > 0;
          })
          .map(([key, value]) =>
            Array.isArray(value)
              ? `${key}:${value.join(',')}`
              : `${key}:${value}`,
          )
      : []),
  ],

  TRIAL_STUDENTS: (params?: GetTrialStudentProps) => [
    KEYS.TRIAL_STUDENTS,
    ...(params
      ? Object.entries(params)
          .filter(Boolean)
          .map(([key, value]) => `${key}:${value}`)
      : []),
  ],

  TRIAL_LIFECYCLE_CONFLICTS: (params?: { page?: number; limit?: number }) => [
    KEYS.TRIAL_LIFECYCLE_CONFLICTS,
    ...(params
      ? Object.entries(params)
          .filter(([, value]) => value !== undefined)
          .map(([key, value]) => `${key}:${value}`)
      : []),
  ],

  LOGIN_IMAGE: (studioId: string) => [KEYS.LOGIN_IMAGE, studioId],

  STUDIO_PREFLIGHT_CHECK: (studioId?: string | null) => [
    KEYS.STUDIO_PREFLIGHT_CHECK,
    studioId,
  ],

  AUDIT_LOGS: (params?: GetAuditLogsParams) => [
    KEYS.AUDIT_LOGS,
    ...(params
      ? Object.entries(params)
          .filter(([, value]) => value !== undefined)
          .map(([key, value]) => `${key}:${value}`)
      : []),
  ],

  OUTGOING_EMAIL_LOGS: (params?: {
    page?: number;
    limit?: number;
    startDate?: string;
    endDate?: string;
    emailScenario?: string;
    status?: string;
    q?: string;
  }) => [
    KEYS.OUTGOING_EMAIL_LOGS,
    ...(params
      ? (Object.entries(params) as Array<[string, string | number | undefined]>)
          .filter(([, value]) => value !== undefined)
          .map(([key, value]) => `${key}:${String(value)}`)
      : []),
  ],

  OUTGOING_EMAIL_LOG_DETAIL: (id: string) => [
    KEYS.OUTGOING_EMAIL_LOGS,
    'detail',
    id,
  ],

  OUTGOING_EMAIL_LOG_SCENARIOS: () => [KEYS.OUTGOING_EMAIL_LOG_SCENARIOS],

  EMAIL_COMPOSER_DEFAULTS: (studioId?: string | null) => [
    KEYS.EMAIL_COMPOSER_DEFAULTS,
    studioId,
  ],

  EMAIL_CAMPAIGN_TEMPLATES: (
    studioId?: string | null,
    category?: string,
  ) => [KEYS.EMAIL_CAMPAIGN_TEMPLATES, studioId, category],

  EMAIL_CAMPAIGNS: (studioId?: string | null, status?: string) => [
    KEYS.EMAIL_CAMPAIGNS,
    studioId,
    status,
  ],

  EMAIL_CAMPAIGN: (studioId?: string | null, id?: string | null) => [
    KEYS.EMAIL_CAMPAIGNS,
    'detail',
    studioId,
    id,
  ],

  EMAIL_CAMPAIGN_RECIPIENTS: (
    studioId?: string | null,
    id?: string | null,
    params?: { page?: number; limit?: number; status?: string },
  ) => [KEYS.EMAIL_CAMPAIGNS, 'recipients', studioId, id, params],

  UNLAYER_TEMPLATES: () => [KEYS.UNLAYER_TEMPLATES],

  UNLAYER_IDENTITY: () => [KEYS.UNLAYER_IDENTITY],

  MOBILE_INVENTOR_PUSH_INTEGRATION: () => [
    KEYS.MOBILE_INVENTOR_PUSH,
    'integration',
  ],

  MOBILE_INVENTOR_PUSH_CONTENT: () => [KEYS.MOBILE_INVENTOR_PUSH_CONTENT],

  MOBILE_INVENTOR_PUSH_SCENARIOS: () => [KEYS.MOBILE_INVENTOR_PUSH_SCENARIOS],

  MOBILE_INVENTOR_PUSH_LOGS: (params?: {
    page?: number;
    limit?: number;
    scenario?: string;
    status?: string;
    startDate?: string;
    endDate?: string;
  }) => [KEYS.MOBILE_INVENTOR_PUSH, 'logs', params ?? {}],

  AUDIT_RESOURCES: () => [KEYS.AUDIT_RESOURCES],

  ANNOUNCEMENTS: (params: GetAnnouncementsParams = {}) => [
    KEYS.ANNOUNCEMENTS,
    params.page,
    params.limit,
    params.search,
    params.sortBy,
    params.sortOrder,
  ],

  AUDIT_EVENTS: () => [KEYS.AUDIT_EVENTS],

  FORM_SUBMISSIONS: (
    studioId: string | null | undefined,
    params?: {
      page?: number;
      limit?: number;
      formType?: string;
      syncStatus?: string;
      search?: string;
      from?: string;
      to?: string;
    },
  ) => [
    KEYS.FORM_SUBMISSIONS,
    studioId ?? 'no-studio',
    ...(params
      ? (Object.entries(params) as Array<[string, string | number | undefined]>)
          .filter(([, value]) => value !== undefined)
          .map(([key, value]) => `${key}:${String(value)}`)
      : []),
  ],

  FORM_SUBMISSION: (studioId: string | null | undefined, id: string) => [
    KEYS.FORM_SUBMISSION,
    studioId ?? 'no-studio',
    id,
  ],

  FORM_CONFIGS: (studioId: string | null | undefined) => [
    KEYS.FORM_CONFIGS,
    studioId ?? 'no-studio',
  ],

  FORM_TEMPLATES: () => [KEYS.FORM_TEMPLATES],

  FORM_PUBLIC_CONFIG: (studioId: string, formType: string, formId?: string) => [
    KEYS.FORM_PUBLIC_CONFIG,
    studioId,
    formType,
    formId,
  ],
} as const;
